import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { defaultRouteForAccess, getUserAccess } from "../utils/authRoles";
import { withTimeout } from "../utils/asyncTimeout";
import { traceAsync } from "../utils/perfTrace";

const AUTH_VERIFY_TIMEOUT_MS = 25000;

const allowedTypes = new Set([
  "signup",
  "magiclink",
  "recovery",
  "invite",
  "email_change",
  "email",
]);

const getSafeRedirectTarget = (value) => {
  if (!value) return "";

  try {
    const parsed = value.startsWith("http")
      ? new URL(value)
      : new URL(value, window.location.origin);

    if (parsed.origin !== window.location.origin) return "";

    const target = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    if (!target.startsWith("/") || target.startsWith("//")) return "";

    return target;
  } catch {
    return "";
  }
};

export default function AuthVerify() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Confirming your portal access...");

  useEffect(() => {
    let mounted = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params.get("token_hash");
      const type = params.get("type") || "signup";
      const redirectTo = getSafeRedirectTarget(params.get("redirect_to"));

      if (!tokenHash || !allowedTypes.has(type)) {
        setMessage("This confirmation link is missing required details.");
        setTimeout(() => navigate("/login", { replace: true }), 1800);
        return;
      }

      const { error } = await traceAsync(
        "verify.verifyOtp",
        () => withTimeout(
          supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          }),
          AUTH_VERIFY_TIMEOUT_MS,
          "Auth verification timed out"
        ),
        { type }
      );

      if (error) {
        console.error("[AuthVerify] verifyOtp:", error);
        if (!mounted) return;
        setMessage("This confirmation link is invalid or has expired. Please request a new one.");
        setTimeout(() => navigate("/login", { replace: true }), 2600);
        return;
      }

      const { data } = await traceAsync(
        "verify.getSession",
        () => withTimeout(
          supabase.auth.getSession(),
          AUTH_VERIFY_TIMEOUT_MS,
          "Auth verification session check timed out"
        ),
        { type }
      );

      if (!mounted) return;

      if (type === "recovery") {
        navigate("/update-password", { replace: true });
        return;
      }

      if (data?.session) {
        const access = await traceAsync("verify.getUserAccess", () => getUserAccess(), { type });
        navigate(redirectTo || defaultRouteForAccess(access), { replace: true });
      } else {
        navigate("/login?confirmed=1", { replace: true });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f4f4f4] px-6 text-center">
      <div className="max-w-sm rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <p className="text-sm font-medium text-gray-700">{message}</p>
      </div>
    </div>
  );
}
